import type { CertGenParams } from '@yandex-int/k-common';
import { useIntl } from 'platform-components';
import { getCertificates } from 'platform-components/src/components/user/selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
    getAllEventSlugsFromConfig,
    getAllWebinarSlugsFromConfig,
    getConfigSlugs,
} from 'common.components/certificate-card/selectors';
import {
    BaseCert,
    BaseCert2025,
    EventCert,
    WebinarCert,
    TeacherTrainingCoursesCert,
    ActiveAutumn2022,
    ActiveSpring2022,
    GLAutumn2022,
    GLInfSpring2022,
    GLInfWinter2023,
    GLInfSpring2024,
    GLKidsAutumn2023,
    GLMathSpring2023,
    GLSpring2022,
    PYTH1LEV,
    StudentDiagInfAutumn2022,
    StudentDiagInf,
    StudentDiagInfSpring2023,
    TeacherDiagInfAutumn2022,
    TeacherDiagInfSpring2023,
    TeacherReserveCandidate,
    TeacherReserveFinalist,
    TeacherReserveProforientation,
    TRBaseCertAutumn2024,
    TeacherDiagAutumn2024,
    StudentDiagInfSpring2025,
    TeacherReserve2025,
    GLInfAutumn2025,
} from 'common.components/certificate-templates';
import {
    getCertExpandedParamsFromViewedHints,
    getCertParams,
    getEventCodesWithDates,
} from 'common.components/user/selectors';

export const useCertRenderer = (slug: string) => {
    const intl = useIntl();

    const webinarsSlugs = useSelector(getAllWebinarSlugsFromConfig);
    const eventSlugs = useSelector(getAllEventSlugsFromConfig);
    const eventsCodes = useSelector(getEventCodesWithDates);

    const configSlugs = useSelector(getConfigSlugs);

    const teacherCertParams = useSelector(getCertParams);
    const certificatesViewedHints = useSelector(getCertificates);
    const viewedHintsParams = useSelector(getCertExpandedParamsFromViewedHints);

    // параметры из viewed_hints, которые нужны сертам кпк
    const teacherTrainingCourseExpandedParams = useMemo(() => {
        const certDataSource =
            (certificatesViewedHints?.training_course_suggestions &&
                certificatesViewedHints.training_course_suggestions[slug]) ||
            (certificatesViewedHints?.hr_reserve_suggestions && certificatesViewedHints.hr_reserve_suggestions[slug]);

        if (!certDataSource) {
            return viewedHintsParams || null;
        }

        return {
            ...viewedHintsParams,
            serial_number: certDataSource.serial_number || '',
            issue_date: certDataSource.issue_date,
        };
    }, [
        viewedHintsParams,
        certificatesViewedHints.training_course_suggestions,
        certificatesViewedHints.hr_reserve_suggestions,
        slug,
    ]);

    // eslint-disable-next-line complexity
    return useMemo(() => {
        const eventDate = eventsCodes.find((event) => event.event_code.startsWith(slug))?.date_created;
        const formattedDate = intl.formatDate(eventDate, { day: 'numeric', month: 'long', year: 'numeric' });

        const defaultWebinarDescription = intl.formatMessage({
            id: 'webinarCert.eventDescription.default',
        });

        const defaultEventDescription = intl.formatMessage({
            id: 'eventCert.eventDescription.default',
            defaultMessage: defaultWebinarDescription,
        });

        let additionalParams: { [key: string]: unknown } = {},
            getCertHTML: ((params: CertGenParams & unknown) => string) | undefined;

        // определяем какой серт нужно отрендерить
        if (teacherCertParams) {
            if (slug === 'base') {
                getCertHTML = BaseCert;
            } else if (webinarsSlugs.includes(slug)) {
                const eventName = intl.formatMessage({ id: `webinarCert.eventName.${slug}` });
                // новый вид сертов в 2025 году
                const isNewCert2025 = eventDate ? new Date(eventDate) > new Date('2025-08-10') : true;
                const eventRegion = intl.formatMessage({ id: `webinarCert.eventRegion.${slug}` });

                getCertHTML = WebinarCert;
                additionalParams = {
                    event_description: intl.formatMessage(
                        {
                            id: `webinarCert.eventDescription.${slug}`,
                            defaultMessage: defaultWebinarDescription,
                        },
                        { name: eventName }
                    ),
                    // если нет ключа, выводим пустую строку
                    event_region: eventRegion === `webinarCert.eventRegion.${slug}` ? '' : eventRegion,
                    event_duration: intl.formatMessage({ id: `webinarCert.eventDuration.${slug}` }),
                    event_date: intl.formatMessage({
                        id: `webinarCert.eventDate.${slug}`,
                        defaultMessage: formattedDate,
                    }),
                    isNewCert2025,
                };
            } else if (eventSlugs.includes(slug)) {
                // Ранее сертификаты за вебинар и мероприятие не отличались,
                // поэтому добавлялись по ключу webinarCert.key

                // новый вид сертов в 2025 году
                const isNewCert2025 = eventDate ? new Date(eventDate) > new Date('2025-08-10') : true;
                const eventRegion = intl.formatMessage({ id: `eventCert.eventRegion.${slug}` });

                const eventNameOld = intl.formatMessage({ id: `webinarCert.eventName.${slug}` });

                const eventName = intl.formatMessage({
                    id: `eventCert.eventName.${slug}`,
                    defaultMessage: eventNameOld,
                });

                const eventDescriptionOld = intl.formatMessage(
                    {
                        id: `webinarCert.eventDescription.${slug}`,
                        defaultMessage: defaultEventDescription,
                    },
                    { name: eventName }
                );

                const eventDescription = intl.formatMessage(
                    {
                        id: `eventCert.eventDescription.${slug}`,
                        defaultMessage: eventDescriptionOld,
                    },
                    { name: eventName }
                );

                const eventDurationOld = intl.formatMessage({ id: `webinarCert.eventDuration.${slug}` });

                const eventDuration = intl.formatMessage({
                    id: `eventCert.eventDuration.${slug}`,
                    defaultMessage: eventDurationOld,
                });

                getCertHTML = EventCert;
                additionalParams = {
                    ...teacherCertParams,
                    event_description: eventDescription,
                    // если нет ключа, выводим пустую строку
                    event_region: eventRegion === `eventCert.eventRegion.${slug}` ? '' : eventRegion,

                    event_duration: eventDuration,
                    event_date: intl.formatMessage({
                        id: `eventCert.eventDate.${slug}`,
                        defaultMessage: formattedDate,
                    }),
                    isNewCert2025,
                };
            } else if (configSlugs?.teacher_training_courses.includes(slug)) {
                if (teacherTrainingCourseExpandedParams) {
                    getCertHTML = TeacherTrainingCoursesCert;
                    additionalParams = {
                        ...teacherTrainingCourseExpandedParams,
                        slug,
                    };
                }
                // серты без условия, введенные по промокоду
            } else if (configSlugs?.certs_without_condition?.includes(slug)) {
                if (slug === 'PYTH1LEV') {
                    getCertHTML = PYTH1LEV;
                    additionalParams = {
                        year: intl.formatDate(eventDate, { year: 'numeric' }),
                    };
                } else if (slug === 'TRMENTOR') {
                    getCertHTML = BaseCert2025;
                    additionalParams = { slug, intl, issue_date: teacherTrainingCourseExpandedParams?.issue_date };
                } else if (slug === 'TDIAUTUMN2024') {
                    getCertHTML = TeacherDiagAutumn2024;
                    additionalParams = { slug };
                } else if (slug === 'KINFU') {
                    getCertHTML = BaseCert2025;
                    additionalParams = { slug };
                }
            } else if (configSlugs?.spring_2022.includes(slug)) {
                getCertHTML = slug === 'ActiveSpring2022' ? ActiveSpring2022 : GLSpring2022;
                additionalParams = { subject: slug };
            } else if (configSlugs?.spring_2022_inf.includes(slug)) {
                getCertHTML = GLInfSpring2022;
            } else if (configSlugs?.autumn_2022.includes(slug)) {
                getCertHTML = slug === 'ActiveAutumn2022' ? ActiveAutumn2022 : GLAutumn2022;
            } else if (configSlugs?.autumn_2022_diag.includes(slug)) {
                getCertHTML = slug === 'TeacherDiagInfAutumn2022' ? TeacherDiagInfAutumn2022 : StudentDiagInfAutumn2022;
            } else if (configSlugs?.winter_2023_inf?.includes(slug)) {
                getCertHTML = GLInfWinter2023;
                additionalParams = { intl };
            } else if (configSlugs?.spring_2023_math?.includes(slug)) {
                getCertHTML = GLMathSpring2023;
                additionalParams = { intl };
            } else if (configSlugs?.spring_2023_diag.includes(slug)) {
                getCertHTML = slug === 'TeacherDiagInfSpring2023' ? TeacherDiagInfSpring2023 : StudentDiagInfSpring2023;
            } else if (configSlugs?.teacher_reserve.includes(slug)) {
                switch (slug) {
                    case 'TeacherReserveFinalist':
                        getCertHTML = TeacherReserveFinalist;
                        additionalParams = { issue_date: teacherTrainingCourseExpandedParams?.issue_date };
                        break;
                    case 'TeacherReserveProforientation':
                        getCertHTML = TeacherReserveProforientation;
                        additionalParams = { issue_date: teacherTrainingCourseExpandedParams?.issue_date };
                        break;
                    default:
                        getCertHTML = TeacherReserveCandidate;
                        additionalParams = { slug, intl, issue_date: teacherTrainingCourseExpandedParams?.issue_date };
                }
            } else if (
                configSlugs?.autumn_2023_diag.includes(slug) ||
                configSlugs?.spring_2024_diag.includes(slug) ||
                configSlugs?.autumn_2024_diag.includes(slug)
            ) {
                getCertHTML = StudentDiagInf;
                additionalParams = { slug };
            } else if (configSlugs?.autumn_2023_olymp.includes(slug)) {
                getCertHTML = GLKidsAutumn2023;
            } else if (configSlugs?.spring_2024_inf.includes(slug)) {
                getCertHTML = GLInfSpring2024;
            } else if (configSlugs?.spring_2024_teacher_reserve_competition.includes(slug)) {
                getCertHTML = TeacherReserveCandidate;
                additionalParams = { slug, intl };
            } else if (configSlugs?.summer_2024_teacher_reserve_award.includes(slug)) {
                getCertHTML = TeacherReserveCandidate;
                additionalParams = { slug, intl };
            } else if (configSlugs?.autumn_2024_teacher_reserve_subject_skills?.includes(slug)) {
                getCertHTML = TRBaseCertAutumn2024;
                additionalParams = { slug };
            } else if (configSlugs?.spring_2025_diag?.includes(slug)) {
                getCertHTML = StudentDiagInfSpring2025;
                additionalParams = { slug };
            } else if (configSlugs?.teacher_reserve_2025?.includes(slug)) {
                getCertHTML = TeacherReserve2025;
                additionalParams = { slug };
            } else if (
                configSlugs?.certs_with_condition.includes(slug) ||
                configSlugs?.training_courses.includes(slug)
            ) {
                getCertHTML = BaseCert2025;
                additionalParams = { slug, issue_date: teacherTrainingCourseExpandedParams?.issue_date };
            } else if (configSlugs?.by_slug?.includes(slug)) {
                getCertHTML = { GLInfAutumn2025 }[slug];
                additionalParams = { slug };
            }
        }

        return getCertHTML ? () => getCertHTML(Object.assign({}, teacherCertParams, additionalParams)) : undefined;
        // не завязываемся на configSlugs, тк их слишком много и они не меняются на протяжении сессии
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        certificatesViewedHints.hr_reserve_suggestions,
        eventSlugs,
        eventsCodes,
        intl,
        slug,
        teacherCertParams,
        teacherTrainingCourseExpandedParams,
        webinarsSlugs,
    ]);
};
